import { BaseViewStore } from 'core/stores';
import { observable, action, makeObservable } from 'mobx';
import { LookupStore } from 'modules/lookup/stores';
import { BulkPrintForm } from 'modules/order/forms';
import { lookupRouteName, sourceOfChange } from 'common/constants';
import { CreateBoxForm } from 'modules/product/forms';
import FulfillmentOrderStore from './FulfillmentOrderStore';
import { ShipmentInfoStore } from 'modules/shipment-info/stores';
import { createFileUrl } from 'core/utils';
import _ from 'lodash';
import { BaasicDropdownViewStore } from 'common/stores';

class BulkPrintViewStore extends BaseViewStore {
	@observable createBoxTemplateSuccessMessage = null;
	@observable createBoxTemplateErrorMessage = null;
	@observable selectedFulfillmentOrders = [];
	boxForm = new CreateBoxForm();
	bulkPrintForm = new BulkPrintForm({
		onSuccess: async () => {
			await this.printBulkList();
		},
	});

	constructor(rootStore, { modalParams }) {
		super(rootStore);
		makeObservable(this);
		this.lookupStore = new LookupStore(rootStore);
		this.fulfillmentOrderStore = new FulfillmentOrderStore(rootStore);
		this.shipmentInfoStore = new ShipmentInfoStore(rootStore);
		this.modalParams = modalParams;
		this.initialize();
	}

	@action.bound
	initialize = async () => {
		await this.getFulfillmentOrders();
		if (
			// If none of the selected Fulfillment Orders has box required, then bulk print immediately, skip choose box step.
			!_.some(this.selectedFulfillmentOrders, fulfillmentOrder => {
				return fulfillmentOrder.isBoxRequired;
			})
		) {
			await this.printBulkList();
		}
	};

	@action.bound
	printBulkList = async () => {
		try {
			this.suspend();
			let bulkPrintFulfillmentOrders = [];
			_.forEach(this.selectedFulfillmentOrders, fulfillmentOrder => {
				const bulkPrintFulfillmentOrder = {
					fulfillmentOrderId: fulfillmentOrder.id,
					boxId: fulfillmentOrder.boxId, // fulfillmentOrder.boxId is set in onBoxChange() function.
				};
				bulkPrintFulfillmentOrders.push(bulkPrintFulfillmentOrder);
			});
			const bulkPrintHistoryItems = _.map(this.selectedFulfillmentOrders, fulfillmentOrder => {
				return { fulfillmentOrderId: fulfillmentOrder.id };
			});
			this.bulkPrintHistory = {
				numberOfFulfillmentOrders: bulkPrintHistoryItems.length,
				bulkPrintHistoryItems: bulkPrintHistoryItems,
			};
			await this.shipmentInfoStore.bulkPrint(bulkPrintFulfillmentOrders, sourceOfChange.shipmentBulkPrintBulkList);
			this.resume();
			this.openPrintWindow = false;

			if (this.shipmentInfoStore.item.data.labels != '') {
				this.openPrintWindow = true;
				this.pdfURL = createFileUrl(this.shipmentInfoStore.item.data.labels);
				if (this.shipmentInfoStore.item.data.successCount > 0 && this.shipmentInfoStore.item.data.failedCount > 0) {
					this.rootStore.notificationStore.error('FULFILLMENTORDER.BULKPRINT.PARTIALYPRINTED');
				} else if (this.shipmentInfoStore.item.data.failedCount > 0) {
					this.rootStore.notificationStore.error('FULFILLMENTORDER.BULKPRINT.EMPTY');
				}
			} else if (this.shipmentInfoStore.item.data.labels === '') {
				this.rootStore.notificationStore.error('FULFILLMENTORDER.BULKPRINT.EMPTY');
				this.openPrintWindow = false;
			}
		} catch (error) {
			this.rootStore.notificationStore.error('FULFILLMENTORDER.BULKPRINT.ERROR');
		}
		this.modalParams.success({
			openPrintWindow: this.openPrintWindow,
			bulkPrintHistory: this.bulkPrintHistory,
			pdfURL: this.pdfURL,
		});
	};

	@action.bound
	getFulfillmentOrders = async () => {
		this.suspend();
		const fulfillmentOrderIds = _.map(this.modalParams.data, item => {
			return item.id;
		});
		this.selectedFulfillmentOrders = await this.fulfillmentOrderStore.getFulfillmentOrders({
			ids: fulfillmentOrderIds,
			embed: ['fulfillmentOrderItems', 'fulfillmentOrderItems.product'],
		});

		await this.prepareData();
		this.resume();
	};

	@action.bound
	prepareData = async () => {
		_.each(this.selectedFulfillmentOrders, fulfillmentOrder => {
			const isBoxRequired =
				fulfillmentOrder.fulfillmentOrderItems.length > 1 ||
				_.some(fulfillmentOrder.fulfillmentOrderItems, item => {
					return item.quantity > 1;
				});

			fulfillmentOrder.isBoxRequired = isBoxRequired;
		});
		this.selectedFulfillmentOrders = _.orderBy(
			this.selectedFulfillmentOrders,
			['isBoxRequired', 'fulfillmentOrderId'],
			['desc', 'desc']
		);
		_.each(this.selectedFulfillmentOrders, (fulfillmentOrder, index) => {
			this.bulkPrintForm.$('fulfillmentOrders').add([
				{
					id: fulfillmentOrder.id,
					fulfillmentOrderId: fulfillmentOrder.fulfillmentOrderId,
					isBoxRequired: fulfillmentOrder.isBoxRequired,
					boxId: null,
					fulfillmentOrderItems: fulfillmentOrder.fulfillmentOrderItems,
				},
			]);

			this.bulkPrintForm.$(`fulfillmentOrders[${index}]`).boxDropdownStore = new BaasicDropdownViewStore({
				field: this.bulkPrintForm.$(`fulfillmentOrders[${index}].boxId`),
				store: new LookupStore(this.rootStore),
				lookupRoute: lookupRouteName.box,
				onChangeCallback: option =>
					this.onBoxChange(option.value, this.bulkPrintForm.$(`fulfillmentOrders[${index}].id`).value),
				fetchInitialValues: fulfillmentOrder.isBoxRequired,
			});
		});
	};

	@action.bound
	onBoxChange = (value, fulfillmentOrderId) => {
		_.each(this.selectedFulfillmentOrders, fulfillmentOrder => {
			if (fulfillmentOrder.id === fulfillmentOrderId) {
				fulfillmentOrder.boxId = value;
			}
		});
	};

	@action.bound
	onCreateBoxTemplate = async () => {
		this.boxForm.validate().then(async ({ isValid }) => {
			try {
				if (isValid) {
					const { name, width, height, length } = this.boxForm.values();

					const boxValues = {
						name,
						width,
						height,
						length,
					};
					const response = await this.lookupStore.createLookup(
						boxValues,
						lookupRouteName.box,
						sourceOfChange.createLookup
					);
					if (response.message) {
						this.boxForm.$('name').invalidate(response.message);
					} else {
						var createdBox = this.lookupStore.item;
						if (createdBox) {
							this.createBoxTemplateSuccessMessage = 'FULFILLMENTORDER.BULKPRINT.CREATEBOX.SUCCESS';
							this.boxForm.$('name').clear();
							this.boxForm.$('height').clear();
							this.boxForm.$('width').clear();
							this.boxForm.$('length').clear();

							this.bulkPrintForm.$('fulfillmentOrders').fields.forEach(field => {
								if (field.value.isBoxRequired) {
									field.boxDropdownStore.reload();
								}
							});
						}
					}
				} else {
					this.boxForm.showErrors(true);
				}
			} catch (error) {
				this.createBoxTemplateErrorMessage = 'FULFILLMENTORDER.BULKPRINT.CREATEBOX.ERROR';
			} finally {
				setTimeout(() => {
					this.createBoxTemplateSuccessMessage = null;
					this.createBoxTemplateErrorMessage = null;
				}, 2500);
			}
		});
	};
}

export default BulkPrintViewStore;
